import { ReactNode } from "react";
import { useTranslation } from "i18n";
import { MediaAssetType } from "mediaModules/media/models";

interface IStorageProgressProps {
  mediaType: MediaAssetType;
  usedInSec: number;
  limitInSec: number;
  icon?: ReactNode;
}

const formatDuration = (
  seconds: number,
  minLabel: string,
  secLabel: string
): string => {
  const safeValue = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeValue / 60);
  const secs = safeValue % 60;

  return `${minutes} ${minLabel} ${secs} ${secLabel}`;
};

export const UploadCapacityProgress = ({
  mediaType,
  usedInSec,
  limitInSec,
  icon,
}: IStorageProgressProps) => {
  const { t } = useTranslation();
  const used = Math.max(0, Math.floor(usedInSec));
  const limit = Math.max(0, Math.floor(limitInSec));
  const remaining = Math.max(0, limit - used);
  const maxMinutes = Math.round(limit / 60);
  const usedPercent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const mediaTypeLabel =
    mediaType === MediaAssetType.Audio
      ? t("performer:media.addNew.capacity.audio")
      : t("performer:media.addNew.capacity.video");
  const minLabel = t("performer:media.addNew.capacity.minLabel");
  const secLabel = t("performer:media.addNew.capacity.secLabel");

  return (
    <div className="flex-1 rounded bg-grey-snow-0 p-3" data-testid={mediaType}>
      <div className="mb-2 flex items-center gap-2 text-s text-color-neutral-one">
        {icon}
        <span>
          {t("performer:media.addNew.capacity.title", {
            minutes: maxMinutes,
            mediaType: mediaTypeLabel,
          })}
        </span>
      </div>

      <div className="h-2 overflow-hidden rounded bg-grey-fog-1">
        <div
          className="h-full bg-color-neutral-one transition-[width] duration-500 ease-out"
          style={{ width: `${usedPercent}%` }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between text-s text-color-neutral-one">
        <span>
          {formatDuration(used, minLabel, secLabel)}{" "}
          {t("performer:media.addNew.capacity.used")}
        </span>
        <span className="font-bold">
          {formatDuration(remaining, minLabel, secLabel)}{" "}
          {t("performer:media.addNew.capacity.remaining")}
        </span>
      </div>
    </div>
  );
};
